import React, { useState } from 'react';
import { Modal, Form, Select, Radio, Button } from 'antd';
import { useTranslation } from 'react-i18next'; // Thêm dòng này
import { TradeAccountApi } from '../type';
import { mixPostV1, mixPostV2, mixPutV1 } from 'services/mixin';
import { toast } from 'react-toastify';

interface InfoDeclarationModalProps {
    open: boolean;
    onCancel: () => void;
    onConfirm: (values: any) => Promise<void>;
    accountData?: TradeAccountApi | null;
}

const InfoDeclarationModal: React.FC<InfoDeclarationModalProps> = ({ open, onCancel, onConfirm, accountData }) => {
    const { t } = useTranslation(); // Khởi tạo hook dịch vụ
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const selectedStatus = Form.useWatch('status', form);
    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            onConfirm(values);
            setLoading(false);
            form.resetFields();
        } catch (errorInfo) {
            console.log('Validation Failed:', errorInfo);
        }
    };

    const statusDotMap = {
        Challenge: '#1890ff',     // Blue
        Verification: '#faad14',  // Orange
        Funded: '#52c41a',        // Green
        Demo: '#722ed1',          // Purple
    };

    const modalTitle = (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginTop: '6px' }}>
            <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '50%',
                backgroundColor: '#fdf3df',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}>
                <svg
                    width={18}
                    height={18}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={"#c9a227"}
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
            </div>
            <div>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#0b1c2e', lineHeight: '1.4' }}>
                    {t('infoDeclarationModal.title')}
                </div>
                <div style={{ fontSize: '13px', color: '#8a93a3', fontWeight: 400, marginTop: '3px', lineHeight: 1.5 }}>
                    {t('infoDeclarationModal.description')}
                </div>
            </div>
        </div>
    );

    const modalFooter = [
        <Button
            key="cancel"
            onClick={onCancel}
            style={{
                flex: 1,
                height: '42px',
                borderRadius: '9px',
                borderColor: '#dfe3e9',
                color: '#3a4250',
                fontSize: '14px',
                fontWeight: 600,
            }}
        >
            {t('infoDeclarationModal.btnCancel')}
        </Button>,
        <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleOk}
            style={{
                flex: 1.3,
                height: '42px',
                borderRadius: '9px',
                backgroundColor: '#c9a227',
                borderColor: '#c9a227',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 700,
            }}
        >
            {t('infoDeclarationModal.btnConfirm')}
        </Button>
    ];

    return (
        <Modal
            open={open}
            title={modalTitle}
            footer={modalFooter}
            onCancel={onCancel}
            width={420}
            centered
            styles={{
                body: { padding: '12px 0 4px 0' },
                footer: { display: 'flex', gap: '10px', paddingTop: '12px' }
            }}
        >
            {accountData && (
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', background: '#f5f7fa', borderRadius: '10px', padding: '10px 12px', marginBottom: '18px' }}>
                    <div style={{ width: 'fit-content', height: '28px', borderRadius: '5px', backgroundColor: '#e6e9ee', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <div style={{ paddingLeft: '8px', paddingRight: '8px', color: `${accountData.platform === 'mt4' ? '#1890ff' : accountData.platform === 'mt5' ? '#faad14' : accountData.platform === 'ctrader' ? '#52c41a' : '#3a4250'}`, fontSize: '16px', fontWeight: 700 }}>
                            {accountData.platform}
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#0b1c2e' }}>{accountData.nickname}</div>
                        <div style={{ fontSize: '12px', color: '#8a93a3' }}>
                            {accountData.account_number} · {t('infoDeclarationModal.balanceLabel')} {accountData.balance}
                        </div>
                    </div>
                </div>
            )}

            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
            >
                <Form.Item
                    name="quymo"
                    label={
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#3a4250' }}>
                            {t('infoDeclarationModal.capitalSizeLabel')} <span style={{ color: '#e24b4a' }}>*</span>
                        </span>
                    }
                    rules={[{ required: true, message: t('infoDeclarationModal.capitalSizeRequired') }]}
                >
                    <Select
                        placeholder={t('infoDeclarationModal.capitalSizePlaceholder')}
                        size="large"
                        style={{ width: '100%' }}
                    >
                        <Select.Option value="5000">$5,000</Select.Option>
                        <Select.Option value="10000">$10,000</Select.Option>
                        <Select.Option value="25000">$25,000</Select.Option>
                        <Select.Option value="50000">$50,000</Select.Option> {/* fixed typo $5,0000 thành $50,000 */}
                        <Select.Option value="100000">$100,000</Select.Option>
                        <Select.Option value="200000">$200,000</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    name="status"
                    label={
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#3a4250' }}>
                            {t('infoDeclarationModal.accountStatusLabel')} <span style={{ color: '#e24b4a' }}>*</span>
                        </span>
                    }
                    rules={[{ required: true, message: t('infoDeclarationModal.accountStatusRequired') }]}
                >
                    <Radio.Group
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px',
                            width: '100%'
                        }}
                    >
                        {['Challenge', 'Verification', 'Funded', 'Demo'].map((status) => {
                            // Kiểm tra trạng thái được chọn dựa vào Form.useWatch
                            const isSelected = selectedStatus === status;

                            return (
                                <Radio.Button
                                    key={status}
                                    value={status}
                                    style={{
                                        height: 'auto',
                                        lineHeight: 'initial',
                                        padding: 0,
                                        border: 'none',
                                        background: 'transparent',
                                        borderRadius: '9px',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '7px',
                                            borderRadius: '9px',
                                            padding: '9px 10px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            width: '100%',
                                            height: '100%',
                                            transition: 'all 0.2s ease',

                                            // Thay đổi style động dựa theo form state
                                            border: isSelected ? '1.5px solid #1677ff' : '1.5px solid #e6e9ee',
                                            color: isSelected ? '#1677ff' : '#3a4250',
                                            background: isSelected ? '#e6f4ff' : '#ffffff',
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                background: statusDotMap[status as keyof typeof statusDotMap] || '#ccc'
                                            }}
                                        />
                                        {status}
                                    </div>
                                </Radio.Button>
                            );
                        })}
                    </Radio.Group>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default InfoDeclarationModal;